import {
  Box,
  Button,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { BiCalendarExclamation } from "react-icons/bi";
import { BsFillCalendarDateFill } from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import { ComplexIconButtonAlert } from "../../../../components/display/SimpleIconAlert";
import TitleWithIcon from "../../../../components/display/TitleWithIcon";
import BookingModal from "../../../../components/modals/bookingModalComponents/components/BookingModal";
import BookingModalContext from "../../../../components/modals/bookingModalComponents/context/BookingModalContext";
import CustomDialog from "../../../../components/wrappers/CustomDrawer";
import { ThemeContext } from "../../../../contexts/ThemeContext";
import ToggleDrawer from "../../../../contexts/ToggleDrawer";
import { APPOINTEMENTS } from "../../../../enums/UrlPaths";
import useUser from "../../../../hooks/useUser";
import { isArrayEmpty, useIsDesktop } from "../../../../utils/Functions";
import useGetHomePageUserAppointments from "../../hooks/useGetHomePageUserAppointments";
import { PetAppointmentCard } from "../UserMobileHomePage";
import NoClinicWrapper from "../../../../components/wrappers/NoClinicWrapper";

export default function UserHomePageAppointments() {
  const { t } = useTranslation();
  const { mode } = useContext(ThemeContext);
  const light = String(mode) === "light";
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const appointmentLimit = isDesktop ? 3 : 2;
  const { profile, currentClinic } = useUser();

  const { data, error, isLoading } = useGetHomePageUserAppointments(profile.id);
  const appointments = data?.appointments || [];
  const totalAppointmentsLength = data?.total ?? 0;
  const appointmentRenderLimit = isDesktop ? 3 : 2;

  const gridItemProps = useMemo(() => {
    const countToRender = Math.min(appointments.length, appointmentRenderLimit);
    if (isLoading || countToRender === 0) {
      return { xs: 12, sm: 6, md: 4 };
    }
    if (countToRender === 1) {
      return { xs: 12 };
    } else if (countToRender === 2) {
      return { xs: 12, sm: 6 };
    } else {
      return { xs: 12, sm: 6, md: 4 };
    }
  }, [appointments.length, isLoading, appointmentRenderLimit]);

  function goToAllAppointments() {
    navigate(APPOINTEMENTS);
  }

  return (
    <Paper
      sx={{
        p: 2,
        height: "100%",
        minHeight: "400px",
        maxHeight: "1200px",
        display: "flex",
        flexDirection: "column",
        bgcolor: light ? "white" : "",
      }}
    >
      <Box>
        <Stack direction={"row"} mb={2} justifyContent={"space-between"}>
          <TitleWithIcon title={"Appointments"}>
            <BsFillCalendarDateFill />
          </TitleWithIcon>
          <Box>
            {!currentClinic.id || isArrayEmpty(appointments) ? null : (
              <ToggleDrawer>
                <CustomDialog
                  color="info"
                  buttonSize={"medium"}
                  buttonVariant="contained"
                  buttonMessage={t(
                    !currentClinic.id ? "ChooseClinic" : "BookAppointment",
                  )}
                  maxWidth={"lg"}
                >
                  <BookingModalContext key={"header-booking-context"}>
                    <BookingModal key={"header-booking-modal"} />
                  </BookingModalContext>
                </CustomDialog>
              </ToggleDrawer>
            )}
          </Box>
        </Stack>
        <Divider />
      </Box>
      <Box sx={{ flexGrow: 1, overflowY: "visible", mb: 2 }}>
        <Grid container spacing={2} sx={{ mt: 2, height: "100%" }}>
          {/*Loading */}
          {isLoading && (
            <Grid
              item
              xs={12}
              sm={12}
              md={12}
              mt="auto"
              mb="auto"
              ml="auto"
              mr="auto"
            >
              <LinearProgress />
            </Grid>
          )}

          {/* Error Alert */}
          {!isLoading && error && (
            <Grid item xs={12} sm={12} md={12} mt="auto" mb="auto">
              <ComplexIconButtonAlert
                message="ErrorFetchingAppointments"
                severity="error"
                icon={<BiCalendarExclamation fontSize="inherit" />}
              />
            </Grid>
          )}

          {!isLoading && !error && isArrayEmpty(appointments) && (
            /* No appointments alert*/
            <Grid item xs={12} sm={12} md={12} mt="auto" mb="auto">
              <ComplexIconButtonAlert
                message="NoAppointments"
                severity="info"
                icon={<BiCalendarExclamation fontSize="inherit" />}
              >
                <NoClinicWrapper>
                  <ToggleDrawer>
                    <CustomDialog
                      color="info"
                      buttonSize={"small"}
                      buttonVariant="contained"
                      buttonMessage={t("Book")}
                      maxWidth={"lg"}
                    >
                      <BookingModalContext key={"header-booking-context"}>
                        <BookingModal key={"header-booking-modal"} />
                      </BookingModalContext>
                    </CustomDialog>
                  </ToggleDrawer>
                </NoClinicWrapper>
              </ComplexIconButtonAlert>
            </Grid>
          )}
          {!isLoading &&
            !error &&
            !isArrayEmpty(appointments) &&
            appointments.slice(0, appointmentLimit).map((appointment) => (
              <Grid item height="100%" key={appointment.id} {...gridItemProps}>
                <PetAppointmentCard
                  appointment={appointment}
                  cardVariant="outlined"
                  cardMinHeight="400px"
                  statusTextMinHeight="65px"
                />
              </Grid>
            ))}
        </Grid>
      </Box>

      {Number(totalAppointmentsLength) > Number(appointments.length) && (
        <Grid container>
          <Grid item xs={12}>
            <Box display="flex" justifyContent="center" sx={{ my: 0.5 }}>
              <Typography variant="body2" sx={{ pt: 0.6 }}>
                {Math.min(appointmentLimit, totalAppointmentsLength)} /{" "}
                {totalAppointmentsLength}
              </Typography>
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  goToAllAppointments();
                }}
              >
                {t("ViewAll")}
              </Button>
            </Box>
          </Grid>
        </Grid>
      )}
    </Paper>
  );
}
